import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, Clock, MapPin, AlertTriangle, CheckCircle2, XCircle, Users, Wrench, Shield, History, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const RIESGO_COLORS = { BAJO: 'bg-green-100 text-green-700', MEDIO: 'bg-yellow-100 text-yellow-700', ALTO: 'bg-orange-100 text-orange-700', CRITICO: 'bg-red-100 text-red-700' };
const RIESGO_BG = { BAJO: 'bg-green-500', MEDIO: 'bg-yellow-500', ALTO: 'bg-orange-500', CRITICO: 'bg-red-500' };
const ESTADO_COLORS = { EN_VALIDACION: 'bg-blue-50 text-blue-700 border border-blue-200', PEND_APROBACION: 'bg-purple-50 text-purple-700 border border-purple-200', APROBADA: 'bg-emerald-50 text-emerald-700 border border-emerald-200', RECHAZADA: 'bg-red-50 text-red-700 border border-red-200' };
const ESTADO_LABELS = { EN_VALIDACION: 'EN VALIDACION', PEND_APROBACION: 'PEND. APROBACION', APROBADA: 'APROBADA', RECHAZADA: 'RECHAZADA' };

export default function AprobadorDetalle() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(null); // 'aprobar' | 'rechazar'
  const [comentario, setComentario] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/actividades-operacionales/${id}`, { headers });
      setA(res.data);
    } catch { navigate('/aprobador/actividades'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [id, token]);

  const handleAction = async (action) => {
    setSaving(true);
    try {
      await axios.post(`${API}/actividades-operacionales/${id}/${action}`, { comentario }, { headers });
      toast.success(action === 'aprobar' ? 'Actividad aprobada' : 'Actividad rechazada');
      setShowModal(null);
      setComentario('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  if (loading || !a) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  const hasHighRisks = a.riesgos?.some(r => r.nivel === 'ALTO' || r.nivel === 'CRITICO');
  const isPending = a.estado === 'PEND_APROBACION';

  return (
    <div className="space-y-6" data-testid="aprobador-detalle">
      <button onClick={() => navigate('/aprobador/actividades')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="btn-volver">
        <ArrowLeft className="w-4 h-4" />Volver
      </button>

      {/* Header + Action buttons */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="text-sm font-mono text-orange-600">{a.codigo}</span>
            <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded ${ESTADO_COLORS[a.estado] || ''}`}>{ESTADO_LABELS[a.estado] || a.estado}</span>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${RIESGO_COLORS[a.nivel_riesgo] || ''}`}>{a.nivel_riesgo}</span>
          </div>
          <h1 className="text-xl font-bold text-slate-900">{a.titulo}</h1>
          {a.descripcion && <p className="text-sm text-slate-500 mt-1">{a.descripcion}</p>}
        </div>
        {isPending && (
          <div className="flex gap-2 shrink-0">
            <button onClick={() => { setShowModal('rechazar'); setComentario(''); }}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
              data-testid="btn-rechazar"><XCircle className="w-4 h-4" />Rechazar</button>
            <button onClick={() => { setShowModal('aprobar'); setComentario(''); }}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="btn-aprobar"><CheckCircle2 className="w-4 h-4" />Aprobar</button>
          </div>
        )}
      </div>

      {/* Semaforos */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-slate-500" /><h3 className="text-sm font-semibold text-slate-700">Semaforos de Cumplimiento</h3></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'PERSONAL', icon: Users, ok: (a.personal?.length || 0) > 0 },
            { label: 'RECURSOS', icon: Wrench, ok: (a.recursos?.length || 0) > 0 },
            { label: 'RIESGOS', icon: AlertTriangle, ok: !hasHighRisks },
            { label: 'GENERAL', icon: CheckCircle2, ok: a.semaforo === 'OK' },
          ].map(s => (
            <div key={s.label} className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><s.icon className="w-4 h-4 text-slate-400" /><span className="text-xs font-semibold text-slate-600">{s.label}</span></div>
              <div className="flex items-center gap-1.5">
                <span className={`text-[10px] font-bold ${s.ok ? 'text-emerald-600' : 'text-amber-600'}`}>{s.ok ? 'OK' : 'ALERTA'}</span>
                <div className={`w-3 h-3 rounded-full ${s.ok ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* High risk alerts */}
      {hasHighRisks && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-amber-500" /><p className="text-sm font-semibold text-amber-800 uppercase">Riesgos Altos/Criticos</p>
            <span className="ml-auto text-xs font-bold text-white bg-amber-500 px-2.5 py-1 rounded-full">{a.riesgos.filter(r => r.nivel === 'ALTO' || r.nivel === 'CRITICO').length} alertas</span></div>
          <div className="space-y-2">
            {a.riesgos.filter(r => r.nivel === 'ALTO' || r.nivel === 'CRITICO').map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100">
                <div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white ${RIESGO_BG[r.nivel]}`}>{r.nivel}</span><span className="text-sm text-slate-700">{r.descripcion}</span></div>
                <span className="text-xs text-slate-400">P:{r.probabilidad} S:{r.severidad}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Informacion General</h3>
          <div className="space-y-2">
            {[
              { l: 'Mandante', v: a.mandante }, { l: 'Empresa(s)', v: a.empresas?.length > 0 ? a.empresas.map(e => e.razon_social).join(', ') : a.empresa_nombre },
              { l: 'Fecha', v: a.fecha_planificada?.split('-').reverse().join('/'), icon: Calendar },
              { l: 'Horario', v: a.hora_inicio && a.hora_termino ? `${a.hora_inicio} - ${a.hora_termino}` : '-', icon: Clock },
              { l: 'Duracion', v: a.duracion_horas ? `${a.duracion_horas}h` : '-' },
              { l: 'Ubicacion', v: a.ubicacion || '-', icon: MapPin },
            ].map(i => (
              <div key={i.l} className="flex items-center justify-between py-1"><span className="text-xs text-slate-400">{i.l}</span>
                <span className="text-sm text-slate-700 flex items-center gap-1">{i.icon && <i.icon className="w-3 h-3 text-slate-400" />}{i.v || '-'}</span></div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Resumen</h3>
          <div className="space-y-2">
            <div className="flex justify-between py-1"><span className="text-xs text-slate-400">Personal</span><span className="text-sm text-slate-700">{a.personal?.length || 0} asignado(s)</span></div>
            <div className="flex justify-between py-1"><span className="text-xs text-slate-400">Recursos</span><span className="text-sm text-slate-700">{a.recursos?.length || 0} asignado(s)</span></div>
            <div className="flex justify-between py-1"><span className="text-xs text-slate-400">Riesgos</span><span className="text-sm text-slate-700">{a.riesgos?.length || 0} identificado(s)</span></div>
            <div className="flex justify-between py-1"><span className="text-xs text-slate-400">Preguntas SSO</span><span className="text-sm text-slate-700">{a.preguntas_sso?.filter(q => q.respuesta).length || 0}/{a.preguntas_sso?.length || 0}</span></div>
          </div>
        </div>
      </div>

      {/* Personal */}
      {a.personal?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Personal ({a.personal.length})</h3></div>
          <table className="w-full"><thead><tr className="border-b"><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">RUT</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">NOMBRE</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">ROL</th></tr></thead>
            <tbody>{a.personal.map(p => <tr key={p.id} className="border-b border-slate-50"><td className="px-3 py-2 text-sm font-mono">{p.rut}</td><td className="px-3 py-2 text-sm">{p.nombre}</td><td className="px-3 py-2 text-sm text-slate-500">{p.rol}</td></tr>)}</tbody></table>
        </div>
      )}

      {/* Recursos */}
      {a.recursos?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3"><Wrench className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Recursos ({a.recursos.length})</h3></div>
          <table className="w-full"><thead><tr className="border-b"><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">CODIGO</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">NOMBRE</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">TIPO</th></tr></thead>
            <tbody>{a.recursos.map(r => <tr key={r.id} className="border-b border-slate-50"><td className="px-3 py-2 text-sm font-mono">{r.codigo}</td><td className="px-3 py-2 text-sm">{r.nombre}</td><td className="px-3 py-2 text-sm text-slate-500">{r.tipo}</td></tr>)}</tbody></table>
        </div>
      )}

      {/* Riesgos */}
      {a.riesgos?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Riesgos ({a.riesgos.length})</h3></div>
          <div className="space-y-2">{a.riesgos.map(r => (
            <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white ${RIESGO_BG[r.nivel]}`}>{r.nivel}</span><div><p className="text-sm text-slate-700">{r.descripcion}</p>{r.medida_control && <p className="text-xs text-slate-400">Control: {r.medida_control}</p>}</div></div>
              <span className="text-xs text-slate-400">P:{r.probabilidad} S:{r.severidad}</span>
            </div>
          ))}</div>
        </div>
      )}

      {/* SSO */}
      {a.preguntas_sso?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3"><Shield className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Preguntas SSO</h3></div>
          <div className="space-y-1">{a.preguntas_sso.map((q, i) => (
            <div key={q.id || i} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
              <span className="text-xs font-bold text-slate-400">{i+1}.</span>
              <p className="text-sm text-slate-700 flex-1">{q.pregunta}</p>
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${q.respuesta === 'SI' ? 'bg-emerald-100 text-emerald-700' : q.respuesta === 'NO' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-400'}`}>{q.respuesta || '-'}</span>
            </div>
          ))}</div>
        </div>
      )}

      {/* Historial */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><History className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Historial de Aprobaciones</h3></div>
        {a.historial?.length > 0 ? (
          <div className="space-y-3">
            {a.historial.map(h => (
              <div key={h.id} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                  h.accion === 'APROBAR' ? 'bg-emerald-100' : h.accion === 'RECHAZAR' ? 'bg-red-100' : 'bg-blue-100'
                }`}>
                  {h.accion === 'APROBAR' ? <Check className="w-4 h-4 text-emerald-600" /> :
                   h.accion === 'RECHAZAR' ? <X className="w-4 h-4 text-red-600" /> :
                   <Clock className="w-4 h-4 text-blue-600" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{h.usuario_nombre}</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${
                      h.accion === 'APROBAR' ? 'bg-emerald-100 text-emerald-700' : h.accion === 'RECHAZAR' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                    }`}>{h.accion === 'ENVIAR_APROBACION' ? 'Enviada' : h.accion === 'APROBAR' ? 'Aprobada' : 'Rechazada'}</span>
                    <span className="text-xs text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString('es-CL') : ''}</span>
                  </div>
                  {h.comentario && <p className="text-sm text-slate-500 mt-0.5">{h.comentario}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 italic">Sin historial de aprobaciones</p>}
      </div>

      {/* Modal Aprobar/Rechazar */}
      {showModal && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-slate-900 mb-1">
              {showModal === 'aprobar' ? 'Aprobar Actividad' : 'Rechazar Actividad'}
            </h2>
            <p className="text-sm text-slate-500 mb-4">{a.codigo} - {a.titulo}</p>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">
                {showModal === 'rechazar' ? 'Motivo del rechazo *' : 'Comentario (opcional)'}
              </label>
              <textarea value={comentario} onChange={e => setComentario(e.target.value)}
                placeholder={showModal === 'rechazar' ? 'Indique el motivo del rechazo...' : 'Comentario opcional...'}
                rows={3} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                data-testid="input-comentario" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowModal(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={() => handleAction(showModal)}
                disabled={saving || (showModal === 'rechazar' && !comentario.trim())}
                className={`px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 ${
                  showModal === 'aprobar' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                }`} data-testid="confirm-action-btn">
                {saving ? 'Procesando...' : showModal === 'aprobar' ? 'Confirmar Aprobacion' : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
